import type { DiagnosticAction, DiagnosticEvent } from './diagnostic-types'

const sensitiveKeyPattern = /authorization|cookie|token|password|passwd|pwd|secret|api[-_]?key|access[-_]?key|credential|phone|mobile|email|idcard|identity|bank/i
const sensitiveValuePattern = /((?:authorization|cookie|token|password|passwd|pwd|secret|api[-_]?key|access[-_]?key|credential|phone|mobile|email|idcard|identity|bank)\s*(?:=|:)\s*)([^\s,;#&]+)/gi
const maxSafeTextLength = 2_000
const maxSafeResponseDepth = 5
const maxSafeResponseNodes = 200
const maxSafeResponseItems = 50

export function sanitizeText(value: string): string {
  return value
    .replace(sensitiveValuePattern, '$1***')
    .replace(/\bBearer\s+\S+/gi, 'Bearer ***')
    .replace(/\b1[3-9]\d{9}\b/g, '***')
    .replace(/\b[\w.%+-]+@[\w.-]+\.[A-Za-z]{2,}\b/g, '***')
    .slice(0, maxSafeTextLength)
}

export function sanitizeUrl(value: string): string {
  try {
    const url = new URL(value, window.location.origin)
    for (const key of url.searchParams.keys()) {
      if (sensitiveKeyPattern.test(key)) url.searchParams.set(key, '***')
    }
    return `${url.pathname}${url.search}${sanitizeText(url.hash)}`
  } catch {
    return sanitizeText(value)
  }
}

export function sanitizeHeaders(headers: Record<string, unknown>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(headers).map(([key, value]) => [key, sensitiveKeyPattern.test(key) ? '***' : String(value)])
  )
}

function sanitizeResponseValue(value: unknown, depth: number, seen: WeakSet<object>, budget: { remaining: number }): unknown {
  if (typeof value === 'string') return sanitizeText(value)
  if (typeof value !== 'object' || value === null) return value
  if (seen.has(value)) return '[circular]'
  if (depth >= maxSafeResponseDepth || budget.remaining <= 0) return '[truncated]'

  budget.remaining -= 1
  seen.add(value)

  if (Array.isArray(value)) {
    const items = value.slice(0, maxSafeResponseItems).map((item) => sanitizeResponseValue(item, depth + 1, seen, budget))
    seen.delete(value)
    return items
  }

  const result: Record<string, unknown> = {}
  for (const [key, item] of Object.entries(value).slice(0, maxSafeResponseItems)) {
    result[key] = sensitiveKeyPattern.test(key) ? '***' : sanitizeResponseValue(item, depth + 1, seen, budget)
  }
  seen.delete(value)
  return result
}

export function sanitizeResponseBody(value: unknown): unknown {
  return sanitizeResponseValue(value, 0, new WeakSet(), { remaining: maxSafeResponseNodes })
}

function formatResponseBody(value: unknown): string | undefined {
  if (typeof value === 'undefined') return undefined
  try {
    return JSON.stringify(sanitizeResponseBody(value), null, 2)
  } catch {
    return '"[unserializable response body]"'
  }
}

function formatAction(event: DiagnosticEvent): string[] {
  if (!event.action) return ['- 关联：后台请求']

  const action = event.action
  const type = action.type === 'click' ? '点击' : '按键'
  const name = action.label ? `「${sanitizeText(action.label)}」` : ''
  const correlation = event.correlation === 'explicit' ? '明确操作' : '最近操作'
  const elapsed = Math.max(0, event.at - action.at)
  const lines = [`- 关联：${correlation}，${elapsed}ms 前${type} ${action.element?.tag ?? '元素'}${name}`]
  const element = formatElement(action)
  if (element) lines.push(`- 元素：${element}`)
  return lines
}

function formatElement(action: DiagnosticAction): string | undefined {
  if (!action.element) return undefined
  const values = [
    action.element.testId ? `data-testid=${sanitizeText(action.element.testId)}` : undefined,
    action.element.dataComponent ? `data-c=${sanitizeText(action.element.dataComponent)}` : undefined,
    action.element.role ? `role=${sanitizeText(action.element.role)}` : undefined,
  ].filter(Boolean)

  return values.join('；') || action.element.tag
}

function formatStack(stack: string): string {
  return stack.split('\n').filter(Boolean).slice(0, 8).map(sanitizeText).join('\n')
}

const categoryLabels = {
  network: '网络错误（无响应）',
  timeout: '请求超时',
  client: '客户端请求错误 (4xx)',
  server: '服务端错误 (5xx)',
  business: '业务失败（HTTP 请求成功）',
} as const

export function formatDiagnosticSummary(event: DiagnosticEvent): string {
  const lines = [
    '## 故障摘要',
    `- 时间：${new Date(event.at).toISOString()}`,
    `- 页面：${sanitizeUrl(event.pageUrl)}`,
    `- 构建：${event.build.commit} (${event.build.mode})`,
    ...(event.occurrences && event.occurrences > 1 ? [`- 重复：${event.occurrences} 次`] : []),
    ...formatAction(event),
    '',
  ]

  if (event.request) {
    lines.push(
      '## 请求失败',
      `- 客户端：${event.request.client}`,
      `- ${event.request.method} ${sanitizeUrl(event.request.path)}`,
      `- 状态：${event.request.status ?? '未知'}，耗时：${event.request.durationMs ?? '未知'}ms`,
      `- 类别：${categoryLabels[event.request.category]}`,
    )
    const input = event.request.input
    if (input?.queryKeys?.length || input?.body?.length) {
      const parts = [
        input.queryKeys?.length ? `查询参数：${input.queryKeys.join(', ')}` : undefined,
        input.body?.length ? `请求体：${input.body.join(', ')}` : undefined,
      ].filter((value): value is string => Boolean(value))
      lines.push(`- 请求输入：${parts.join('；')}`)
    }
    const responseBody = formatResponseBody(event.request.responseBody)
    if (responseBody) {
      lines.push('## 服务端响应', '```json', responseBody, '```')
    } else if (event.request.responseCode || event.request.responseMessage) {
      const responseSummary = [event.request.responseCode, event.request.responseMessage]
        .filter((value): value is string => Boolean(value))
        .map(sanitizeText)
        .join('，')
      lines.push(`- 服务端：${responseSummary}`)
    }
    if (event.request.traceId) lines.push(`- traceId：${sanitizeText(event.request.traceId)}`)
    lines.push('')
  }

  lines.push('## 前端错误', `- ${sanitizeText(event.error.name)}：${sanitizeText(event.error.message)}`)
  if (event.error.stack) lines.push(`- 堆栈：${formatStack(event.error.stack)}`)
  return lines.join('\n')
}
